export const CHECKOUT_URL = 'https://histology-pathology-visual-atlas.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '1175860498467444'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/k6kDjmLBtP7Mi2fGMucUo9/Diseno_sin_titulo-11.webp'

export interface InsideImage {
  src: string
  alt: string
}

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/ffePujbAHEbWQ5ZbrBjtvJ/Captura_de_pantalla_2026-09-29_a_la(s)_7.12.12_p._m..png',
    alt: 'Recognizing an inflammatory liver pattern — labeled page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/jXpUWGsPJkRF5L5tBbrKHw/Captura_de_pantalla_2026-09-29_a_la(s)_7.12.30_p._m..png',
    alt: 'Recognizing enlarged airspaces — normal lung vs emphysematous-type change',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/9ALtxdURjjjEdFUfxrih9C/Captura_de_pantalla_2026-09-29_a_la(s)_7.12.46_p._m..png',
    alt: 'Normal GI architecture — stomach, small intestine and colon baseline',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/g55dQLi5Hd4VNthyrgfeUs/Captura_de_pantalla_2026-09-29_a_la(s)_7.13.12_p._m..png',
    alt: 'Preserved organization vs architectural disruption — lymphoid tissue',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/s3ASdZn5RoUKJM3Bgj7QxM/Captura_de_pantalla_2026-09-29_a_la(s)_7.13.29_p._m..png',
    alt: 'Fibrosis — recognize the architectural consequence in four organs',
  },
]

export interface PainPoint {
  title: string
  paragraphs: string[]
  callout?: string
}

export const painPoints: PainPoint[] = [
  {
    title: '😰 EVERYTHING STARTS TO LOOK THE SAME',
    paragraphs: [
      'Pink tissue. Purple nuclei. Glands. Cells. Fibers. Spaces.',
      "You studied the definitions—but when the labels disappear, identifying exactly what you're looking at becomes much harder.",
    ],
  },
  {
    title: '⚠️ YOU KNOW THE THEORY—BUT CAN YOU RECOGNIZE IT?',
    paragraphs: [
      'You can read about epithelial tissues, inflammation, necrosis, dysplasia, or tumor morphology.',
      'But recognizing those features in a microscopic image requires something different: knowing exactly what to look for and what separates one pattern from another.',
    ],
  },
  {
    title: '📚 YOUR STUDY RESOURCES ARE EVERYWHERE',
    paragraphs: [
      'Lecture slides. Textbooks. Microscopy images. Notes. PDFs. Videos. Screenshots. Online references.',
      "The information exists—but it's scattered across different resources and rarely organized around visual recognition and comparison.",
    ],
    callout:
      "You don't need more scattered information. You need one organized visual study system.",
  },
  {
    title: "😩 STUDYING MORE DOESN'T ALWAYS MEAN RECOGNIZING MORE",
    paragraphs: [
      'Reading definitions, reviewing notes, memorizing structures, searching for images, comparing different slides, and trying to remember which features actually matter.',
      "Then a different image appears—and you're unsure again.",
    ],
    callout:
      'Your study time should help you recognize what you\'re seeing—not just memorize another page of theory.',
  },
]

export type ModuleColor = 'blue' | 'red' | 'amber' | 'green' | 'purple' | 'charcoal'

export interface Module {
  title: string
  body: string
  color: ModuleColor
}

export const modules: Module[] = [
  {
    title: 'HISTOLOGY FOUNDATIONS & TISSUE RECOGNITION',
    body: 'Build the visual foundation for recognizing epithelial tissue, connective tissue, muscle, nervous tissue, blood, vessels, staining patterns, microscopic structures, and the defining features that separate one tissue from another.',
    color: 'blue',
  },
  {
    title: 'NORMAL ORGAN HISTOLOGY ATLAS',
    body: 'Explore the normal microscopic architecture of major organs and systems, with key landmarks, labeled structures, low- and high-power features, recognition clues, and common look-alikes.',
    color: 'red',
  },
  {
    title: 'CELL INJURY, INFLAMMATION & TISSUE REPAIR',
    body: 'See how cells and tissues change through adaptation, injury, necrosis, apoptosis, acute and chronic inflammation, granulomatous inflammation, healing, granulation tissue, fibrosis, and repair.',
    color: 'amber',
  },
  {
    title: 'NEOPLASIA & TUMOR MORPHOLOGY ATLAS',
    body: 'Compare normal organization, dysplasia, differentiation, cellular atypia, benign- and malignant-associated morphology, tumor architecture, invasion-related changes, and major neoplastic patterns through structured visual references.',
    color: 'green',
  },
  {
    title: 'ORGAN PATHOLOGY VISUAL ATLAS',
    body: 'Compare normal and altered morphology across major organs including lung, liver, kidney, gastrointestinal tract, thyroid, breast, prostate, skin, cardiovascular tissues, and lymphoid tissue—with visual clues showing what changed.',
    color: 'purple',
  },
  {
    title: 'DIFFERENTIAL RECOGNITION & MICROSCOPY CHALLENGES',
    body: 'Put your recognition skills into practice with tissue identification, normal vs abnormal comparisons, look-alike differentiation, low- vs high-power analysis, feature spotting, unknown-slide challenges, and structured visual reasoning.',
    color: 'charcoal',
  },
]

export interface Bonus {
  number: number
  title: string
  body: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'HISTOLOGY & PATHOLOGY QUICK REFERENCE GUIDE™',
    body: 'Quickly review defining tissue features, organ landmarks, cellular changes, inflammatory patterns, neoplastic morphology, common look-alikes, and essential microscopic recognition clues.',
  },
  {
    number: 2,
    title: 'NORMAL vs ABNORMAL COMPARISON HANDBOOK™',
    body: 'See what changes from normal to abnormal through structured side-by-side comparisons covering cellular injury, inflammation, repair, dysplasia, neoplasia, and major organ pathology patterns.',
  },
  {
    number: 3,
    title: 'HISTOLOGY LOOK-ALIKES DIFFERENTIATION GUIDE™',
    body: 'Learn how to separate commonly confused tissues, organs, structures, and morphological patterns using defining features, supporting clues, and side-by-side differentiation.',
  },
  {
    number: 4,
    title: 'MICROSCOPY RECOGNITION PRACTICE WORKBOOK™',
    body: 'Practice tissue identification, organ recognition, structure identification, normal vs altered patterns, look-alike differentiation, low- to high-power analysis, and unknown-slide recognition with explained answers.',
  },
  {
    number: 5,
    title: 'HISTOLOGY & PATHOLOGY PRACTICAL EXAM PREP KIT™',
    body: 'Organize your practical revision with visual landmark guides, recognition checklists, look-alike reviews, practical stations, mixed recognition exercises, answer keys, weak-spot tracking, and structured review plans.',
  },
]

export interface Differentiator {
  icon: string
  title: string
  body: string
}

export const differentiators: Differentiator[] = [
  {
    icon: '🔬',
    title: 'Practical visual references',
    body: 'Find the tissue, structure, landmark, cellular change, or morphological pattern you need while studying histology and pathology.',
  },
  {
    icon: '🔍',
    title: 'Structured recognition system',
    body: 'Move from normal tissues and organs to cellular changes, pathology patterns, comparisons, and visual recognition.',
  },
  {
    icon: '⚡',
    title: '6 visual study areas',
    body: 'Tissue recognition, organ histology, cell injury, inflammation, neoplasia, organ pathology, and differential recognition.',
  },
  {
    icon: '🔄',
    title: 'Ready to use again and again',
    body: 'Return to the system whenever you need to identify, compare, differentiate, practice, or review a microscopic pattern.',
  },
]

export const offerBullets = [
  '6 comprehensive modules',
  '5 strategic bonuses',
  'Tissue & microscopic recognition',
  'Normal organ histology',
  'Normal vs abnormal comparisons',
  'Cell injury, inflammation & repair',
  'Neoplasia & organ pathology',
  'Differential recognition practice',
]

export interface Testimonial {
  name: string
  role: string
  location: string
  quote: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Chloe Harrison, 22',
    role: 'Medical student, 2nd year',
    location: 'Leeds, United Kingdom',
    quote:
      'In histology labs everything looked like pink and purple noise to me. The normal organ atlas showed me which landmarks to find first at low power, and the look-alikes pages finally taught me how to tell thyroid from kidney cortex or esophagus from skin. In my practical spotter exam I recognized slides I would have guessed on a month earlier.',
  },
  {
    name: 'Daniel Okafor, 27',
    role: 'Pathology resident, first year',
    location: 'Houston, United States',
    quote:
      'Starting residency, I needed a fast way to rebuild my normal histology baseline before sign-out. The normal vs abnormal handbook puts both side by side, so I stop calling reactive changes something worse. The fibrosis and lymphoid architecture pages alone changed how I describe what I see to my attendings.',
  },
  {
    name: 'Priya Nair, 24',
    role: 'Medical laboratory science student',
    location: 'Toronto, Canada',
    quote:
      'I had lecture slides, textbook images and random screenshots saved everywhere. Now everything is organized in one place by tissue and by pattern. The recognition checklists in the practical exam kit tell me exactly what to look for, and my confidence at the microscope went up noticeably.',
  },
  {
    name: 'Megan Clarke, 20',
    role: 'Biomedical science student, 2nd year',
    location: 'Brisbane, Australia',
    quote:
      'Epithelium types were my weak spot. I could recite the definitions but froze when the labels were removed. The tissue recognition module and the practice workbook with explained answers made it click. I study on my iPad between classes and it feels structured instead of overwhelming.',
  },
  {
    name: 'Tyler Brooks, 31',
    role: 'Histotechnology student, career changer',
    location: 'Denver, United States',
    quote:
      'Coming back to school after years in another field, I was nervous about the microscopy side. The quick reference guide and the staining pattern pages helped me understand what a good section should look like and what changes when tissue is altered. It is the resource I open before every lab session.',
  },
]

export interface Faq {
  q: string
  a: string
}

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Medical students, histology and pathology students, biomedical science students, medical laboratory science students, histotechnology students, pathology trainees, and health-science learners studying microscopic tissue recognition.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. HISTOLOGY & PATHOLOGY VISUAL ATLAS™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is a visual educational and study reference system.',
  },
  {
    q: 'Is it another theory-heavy ebook?',
    a: 'No. It focuses on organized visual references, labeled structures, normal vs abnormal comparisons, microscopic landmarks, look-alike differentiation, and structured recognition practice.',
  },
  {
    q: 'What areas are included?',
    a: 'Foundational tissues, normal organ histology, cellular injury, inflammation, tissue repair, neoplasia, tumor morphology, organ pathology, differential recognition, and microscopy practice.',
  },
  {
    q: 'Does this replace formal medical education?',
    a: 'No. It is an educational study resource and does not replace formal medical education, supervised laboratory or clinical training, institutional guidance, diagnostic procedures, or professional judgment.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: 'What devices can I use it on?',
    a: 'Any device that opens PDF files — phone, tablet, laptop or desktop. You can also print the reference pages and comparison charts you use most.',
  },
  {
    q: 'Do I need previous histology or pathology knowledge?',
    a: 'No. The system starts with foundational tissues and normal organ histology, then moves into cellular changes, pathology patterns and recognition practice, so both early and advanced learners can use it.',
  },
  {
    q: 'How long do I keep access, and what if it is not for me?',
    a: 'Access is yours to keep and review whenever you need it. If it is not right for you, contact us within 7 days for a full refund according to the refund terms.',
  },
]

export interface PopupSale {
  name: string
  city: string
  country: string
}

export const popupSales: PopupSale[] = [
  { name: 'Emily W.', city: 'Manchester', country: 'United Kingdom' },
  { name: 'James C.', city: 'Toronto', country: 'Canada' },
  { name: 'Sophia M.', city: 'Austin', country: 'United States' },
  { name: 'Oliver B.', city: 'Melbourne', country: 'Australia' },
  { name: 'Grace T.', city: 'Glasgow', country: 'United Kingdom' },
  { name: 'Ethan R.', city: 'Vancouver', country: 'Canada' },
  { name: 'Ava S.', city: 'Chicago', country: 'United States' },
  { name: 'Liam H.', city: 'Brisbane', country: 'Australia' },
  { name: 'Charlotte D.', city: 'Bristol', country: 'United Kingdom' },
  { name: 'Noah P.', city: 'Montreal', country: 'Canada' },
  { name: 'Isabella R.', city: 'Miami', country: 'United States' },
  { name: 'Jack M.', city: 'Perth', country: 'Australia' },
]
